/** The shell's one listener for the registry's global shortcuts (search, new project, go to, the cheat sheet). Mounted once. */
import { useEffect, useRef } from 'react';

import { getPreferencesSnapshot } from '../../features/preferences/preferencesStore';
import { mainRailShortcutsBlocked } from '../navigation/useMainRailShortcuts';
import { focusHeaderSearch } from '../search/searchHotkeys';
import { SHORTCUTS, comboMatches, createShortcutMatcher, isTextEntryTarget, type GlobalShortcutAction } from './shortcutRegistry';

const TYPING_SHORTCUTS = SHORTCUTS.filter((shortcut) => shortcut.action && shortcut.whileTyping);

export const SHORTCUTS_OPEN_EVENT = 'shortcuts:open';
/** Asks the shell to open or close QuantisAI, the same as its header button. */
export const QUANTISAI_TOGGLE_EVENT = 'quantisai:toggle';

function navigate(view: string): void {
  window.dispatchEvent(new CustomEvent('app:navigate', { detail: { view } }));
}

/** Runs an action, returning false when it had nothing to do so the browser keeps the key. */
function run(action: GlobalShortcutAction): boolean {
  switch (action) {
    case 'search':
      // With the preference off, or no search field on screen, Cmd/Ctrl+K stays the browser's.
      return getPreferencesSnapshot().advanced.cmdKSearch && focusHeaderSearch();
    case 'new-project':
      navigate('add-project');
      return true;
    case 'go-home':
      navigate('dashboard');
      return true;
    case 'go-projects':
      navigate('projects');
      return true;
    case 'show-shortcuts':
      window.dispatchEvent(new CustomEvent(SHORTCUTS_OPEN_EVENT, { detail: { toggle: true } }));
      return true;
    case 'toggle-quantisai':
      window.dispatchEvent(new CustomEvent(QUANTISAI_TOGGLE_EVENT));
      return true;
  }
}

export function useGlobalShortcuts({ canAddProject }: { canAddProject: boolean }): void {
  const canAddProjectRef = useRef(canAddProject);
  canAddProjectRef.current = canAddProject;

  useEffect(() => {
    const matcher = createShortcutMatcher();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (isTextEntryTarget(target) || isTextEntryTarget(active)) {
        matcher.reset();
        if (event.isComposing || event.repeat) return;
        const typed = TYPING_SHORTCUTS.find((shortcut) => shortcut.keys.some((combo) => comboMatches(event, combo)));
        if (typed && run(typed.action!)) event.preventDefault();
        return;
      }
      const shortcut = matcher.match(event);
      if (!shortcut?.action) return;
      if (shortcut.action === 'new-project' && !canAddProjectRef.current) return;

      const withModifier = event.metaKey || event.ctrlKey;
      if (withModifier) {
        if (run(shortcut.action)) event.preventDefault();
        return;
      }
      // Plain keys (? and the G sequences) give way to any open dialog or menu, and to a view that
      // claims the key for itself (Takeoff's own ? list) by calling preventDefault after us.
      if (mainRailShortcutsBlocked()) return;
      window.setTimeout(() => {
        if (!event.defaultPrevented) run(shortcut.action!);
      }, 0);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('blur', matcher.reset);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('blur', matcher.reset);
    };
  }, []);
}
